import {
  clipPath,
  err,
  ok,
  parseClip,
  parseResolvedUrl,
  resolveInstanceUrl,
  RESOLVE_URL_PATH,
  withQuery,
  type AbortLike,
  type ClipTarget,
  type ResolvedTarget,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import type { ApiProblem } from './videos';

/**
 * §8's two resolution endpoints. Request plumbing only — which URL to ask about, and what
 * an empty body means, are both core decisions.
 *
 * One arm is new here rather than shared: **`/api/v1/clips/{id}` answers `200` with zero bytes** for a
 * malformed clip id, and the generic `malformed-json` reading of that ("usually a proxy or captive
 * portal answering instead of Invidious") is the wrong sentence about a link somebody pasted. So an
 * empty body on these two endpoints is reported as `not-resolvable`, which the screen turns into "that
 * is not a clip this instance can find".
 */
export type ResolveProblem = ApiProblem | { readonly kind: 'not-resolvable' };

export type ResolvedTargetResult = Result<ResolvedTarget, ResolveProblem>;
export type ClipResult = Result<ClipTarget, ResolveProblem>;

/**
 * `youtubeUrl` comes from `channelLookupUrl(route)` — core's builder — rather than being assembled
 * here. The endpoint accepts nothing else: a bare `/@handle`, a `youtu.be` link and this instance's own
 * watch URL all answer `400 {"error":"Unknown url"}`.
 */
export async function fetchResolvedTarget(
  platform: Platform,
  youtubeUrl: string,
  signal?: AbortLike,
): Promise<ResolvedTargetResult> {
  const base = resolveInstanceUrl(platform.config, RESOLVE_URL_PATH);
  if (base === null) return err({ kind: 'bad-url', path: RESOLVE_URL_PATH });

  const url = withQuery(base, { url: youtubeUrl });
  if (url === null) return err({ kind: 'bad-url', path: RESOLVE_URL_PATH });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseResolvedUrl(body.value);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

export async function fetchClip(
  platform: Platform,
  clipId: string,
  signal?: AbortLike,
): Promise<ClipResult> {
  const path = clipPath(clipId);
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseClip(body.value);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

async function fetchJson(
  platform: Platform,
  url: string,
  signal?: AbortLike,
): Promise<Result<unknown, ResolveProblem>> {
  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  // Checked before `JSON.parse` rather than caught after it, because on these two endpoints the empty
  // body is a *documented* answer with a specific meaning rather than a broken response: measured
  // `200` with zero bytes for `/api/v1/clips/Ugkx-IlwJ6DCE0r1xfAz`.
  if (response.value.body.trim() === '') return err({ kind: 'not-resolvable' });

  try {
    return ok(JSON.parse(response.value.body) as unknown);
  } catch {
    return err({ kind: 'malformed-json' });
  }
}
